import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getLang } from 'shared/store/selectors/lang.selector';
import classNames from 'classnames';
import styles from './settings.module.scss';
import { Divider, ScreenTitle } from 'src/components/design-system';
import { NavLink } from 'react-router-dom';
import { ROUTES } from 'shared/constants';
import { Link } from 'react-router-dom';
import { isAccountAdmin, isRoot } from 'shared/store/selectors/auth.selector';
import { isAWorkspaceManager } from 'shared/store/selectors/workspace.selector';
import {
  isQualityMetricsEnabled,
  isScheduleAssignmentsEnabled,
} from 'shared/store/selectors/features.selector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faWandMagicSparkles,
  faLayerGroup,
  faFileArrowUp,
  faSitemap,
  faCalendarArrowUp,
  faChartLineUp,
} from '@fortawesome/pro-thin-svg-icons';

export function AllSettingsButton() {
  const lang = useSelector(getLang('SETTINGS'));

  return (
    <Link to={ROUTES.SETTINGS} className={styles.allSettingButton}>
      {lang.TITLE}
    </Link>
  );
}

export const SettingsPage = () => {
  const lang = useSelector(getLang('SETTINGS'));

  const isRootUser = useSelector(isRoot);
  const isAdmin = useSelector(isAccountAdmin);
  const isWSManager = useSelector(isAWorkspaceManager);
  const isAssignmentsEnabled = useSelector(isScheduleAssignmentsEnabled);
  const isQualityMetricsVisible = useSelector(isQualityMetricsEnabled);
  const canViewWorkspaceManagement = isAdmin || isWSManager;

  const routes = useMemo(
    () => [
      {
        path: ROUTES.SETTINGS_WORKSPACES,
        title: lang.WORKSPACE_TITLE,
        subtitle: lang.WORKSPACE_SUBTITLE,
        icon: faLayerGroup,
        hide: !canViewWorkspaceManagement,
      },
      {
        path: ROUTES.SETTINGS_MAGIC_LINK,
        title: lang.MAGIC_LINK,
        subtitle: lang.MAGIC_LINK_SUBTITLE,
        icon: faWandMagicSparkles,
        hide: !isRootUser,
      },
      {
        path: ROUTES.SETTINGS_CONTACT_UPLOAD,
        title: lang.CONTACT_UPLOAD,
        subtitle: lang.CONTACT_UPLOAD_SUBTITLE,
        icon: faFileArrowUp,
        hide: !isRootUser,
      },
      {
        path: ROUTES.SETTINGS_ORGANIZATIONAL_STRUCTURE,
        title: lang.ORG_STRUCTURE_TITLE,
        subtitle: lang.ORG_STRUCTURE_SUBTITLE,
        icon: faSitemap,
        hide: !isAdmin,
      },
      {
        path: ROUTES.SETTINGS_UPLOAD_SHIFTS,
        title: lang.SHIFTS_UPLOAD,
        subtitle: lang.SHIFTS_UPLOAD_SUBTITLE,
        icon: faCalendarArrowUp,
        hide: !isAdmin || !isAssignmentsEnabled,
      },
      {
        path: ROUTES.SETTINGS_QUALITY_METRICS,
        title: lang.QUALITY_METRICS_TITLE,
        subtitle: lang.QUALITY_METRICS_SUBTITLE,
        icon: faChartLineUp,
        hide: !isRootUser || !isQualityMetricsVisible,
      },
    ],
    [
      lang,
      isRootUser,
      isAdmin,
      isAssignmentsEnabled,
      isQualityMetricsVisible,
      canViewWorkspaceManagement,
    ],
  );

  return (
    <div className={classNames(styles.root, 'new-design-system')}>
      <ScreenTitle title={lang.TITLE} />
      <Divider />
      <div className={styles.views}>
        {routes.map(route => {
          return route.hide ? null : (
            <NavLink
              className={styles.viewButton}
              exact
              key={route.path}
              to={route.path}
              aria-label={route.title}>
              <div className={styles.viewButtonTitle}>
                <FontAwesomeIcon icon={route.icon} className={styles.icon} size="xl" />
                <h2>{route.title}</h2>
              </div>
              <p className={styles.subtitle}>{route.subtitle}</p>
            </NavLink>
          );
        })}
      </div>
    </div>
  );
};
